/**
 * IXD 값 편집기 본문 - 목차 클릭 스크롤 + scroll spy.
 *
 * 본문은 워크북 비교 여부와 상관없이 같은 컴포넌트(CompareTableRows)가 그리고,
 * 블록마다 `data-compare-chunk-id` 앵커를 단다. 이 훅은 그 앵커만 보므로 비교
 * 중이 아니어도(= 워크북을 고르기 전에도) 그대로 쓴다.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import type { IxdChunk } from "@/apis/ixd-editor/types";
import { computeCurrentOrderedSectionId } from "@/utils/ixd-dsd-compare/scroll-spy-utils";

const USER_SCROLL_KEYS = new Set([
  "ArrowDown",
  "ArrowUp",
  "End",
  "Home",
  "PageDown",
  "PageUp",
  " ",
]);
const SIDEBAR_NAVIGATION_CORRECTION_DELAY_MS = 180;

const getChunkTargetScrollTop = (
  container: HTMLDivElement,
  element: HTMLElement,
) => {
  const elementTop =
    element.getBoundingClientRect().top -
    container.getBoundingClientRect().top +
    container.scrollTop;

  return Math.min(
    Math.max(elementTop, 0),
    Math.max(container.scrollHeight - container.clientHeight, 0),
  );
};

export const useCompareScrollSpy = (
  chunks: IxdChunk[],
  /**
   * 프로그래매틱 스크롤을 접을 때 같이 접을 화면 쪽 보정 작업.
   * 사용자가 직접 스크롤해 이동을 취소한 경우에도 불린다.
   */
  onCancelProgrammaticScroll?: () => void,
) => {
  const [spyChunkId, setSpyChunkId] = useState<string | null>(null);
  const listContainerRef = useRef<HTMLDivElement>(null);
  const scrollSpyFrameRef = useRef<number | null>(null);
  const navigationCorrectionTimerRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const chunkElementsCacheRef = useRef<{
    container: HTMLDivElement;
    chunks: IxdChunk[];
    chunkIds: string[];
    elements: Map<string, HTMLElement>;
  } | null>(null);

  // 목차 클릭으로 인한 프로그래매틱 스크롤 중에는 scroll spy가 중간 chunk들로
  // 하이라이트를 덮어쓰지 않도록 스크롤이 실제로 끝날 때까지 막는다.
  const isProgrammaticScrollRef = useRef(false);

  // 콜백은 ref 로 들고 있는다 — cancelProgrammaticScroll 의 참조가 매 렌더
  // 바뀌면 스크롤 리스너 effect 가 전부 다시 붙는다.
  const onCancelRef = useRef(onCancelProgrammaticScroll);
  onCancelRef.current = onCancelProgrammaticScroll;

  const cancelProgrammaticScroll = useCallback(() => {
    onCancelRef.current?.();
    if (navigationCorrectionTimerRef.current !== null) {
      clearTimeout(navigationCorrectionTimerRef.current);
      navigationCorrectionTimerRef.current = null;
    }
    isProgrammaticScrollRef.current = false;
  }, []);

  const scrollToChunk = useCallback(
    (chunkId: string) => {
      cancelProgrammaticScroll();
      setSpyChunkId(chunkId);

      const container = listContainerRef.current;
      const element = container?.querySelector(
        `[data-compare-chunk-id="${CSS.escape(chunkId)}"]`,
      );
      if (!container || !(element instanceof HTMLElement)) return;

      isProgrammaticScrollRef.current = true;
      const initialTargetTop = getChunkTargetScrollTop(container, element);

      // 실제 스크롤은 한 번에 끝낸다. 중간 표들을 지나며 scroll event와 lazy
      // renderer 마운트를 연쇄적으로 발생시키지 않는다.
      container.scrollTop = initialTargetTop;

      // lazy target 렌더가 끝난 뒤 좌표를 딱 한 번 다시 읽는다. 메인 스레드가
      // 렌더 중이면 timeout도 자연스럽게 뒤로 밀리므로 완료 후 최종 위치를 맞춘다.
      navigationCorrectionTimerRef.current = setTimeout(() => {
        navigationCorrectionTimerRef.current = null;
        const currentContainer = listContainerRef.current;
        const finalElement = currentContainer?.querySelector(
          `[data-compare-chunk-id="${CSS.escape(chunkId)}"]`,
        );
        if (currentContainer && finalElement instanceof HTMLElement) {
          currentContainer.scrollTop = getChunkTargetScrollTop(
            currentContainer,
            finalElement,
          );
        }
        isProgrammaticScrollRef.current = false;
      }, SIDEBAR_NAVIGATION_CORRECTION_DELAY_MS);
    },
    [cancelProgrammaticScroll],
  );

  useEffect(() => {
    const container = listContainerRef.current;
    if (!container) return;

    const cancelOnPointerIntent = () => cancelProgrammaticScroll();
    const cancelOnScrollKey = (event: KeyboardEvent) => {
      if (!USER_SCROLL_KEYS.has(event.key) || event.metaKey || event.ctrlKey) {
        return;
      }
      const target = event.target;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        (target instanceof HTMLElement && target.isContentEditable)
      ) {
        return;
      }
      cancelProgrammaticScroll();
    };

    container.addEventListener("wheel", cancelOnPointerIntent, {
      passive: true,
    });
    container.addEventListener("pointerdown", cancelOnPointerIntent, {
      passive: true,
    });
    container.addEventListener("touchstart", cancelOnPointerIntent, {
      passive: true,
    });
    document.addEventListener("keydown", cancelOnScrollKey);
    return () => {
      container.removeEventListener("wheel", cancelOnPointerIntent);
      container.removeEventListener("pointerdown", cancelOnPointerIntent);
      container.removeEventListener("touchstart", cancelOnPointerIntent);
      document.removeEventListener("keydown", cancelOnScrollKey);
    };
  }, [cancelProgrammaticScroll, chunks]);

  // 실제 스크롤 DOM에서 직접 호출한다. 레이아웃 변경으로 scroll container가
  // 교체돼도 부모 effect의 오래된 이벤트 연결이 남지 않게 한다.
  const handleTableScroll = useCallback(
    (container: HTMLDivElement) => {
      if (isProgrammaticScrollRef.current) return;
      if (scrollSpyFrameRef.current !== null || chunks.length === 0) {
        return;
      }

      scrollSpyFrameRef.current = window.requestAnimationFrame(() => {
        scrollSpyFrameRef.current = null;
        if (isProgrammaticScrollRef.current) return;

        let cache = chunkElementsCacheRef.current;
        if (
          !cache ||
          cache.container !== container ||
          cache.chunks !== chunks ||
          cache.elements.size !== cache.chunkIds.length
        ) {
          cache = {
            container,
            chunks,
            chunkIds: chunks.map((chunk) => chunk.id),
            elements: new Map(
              Array.from(
                container.querySelectorAll<HTMLElement>(
                  "[data-compare-chunk-id]",
                ),
              ).flatMap((element) => {
                const id = element.dataset.compareChunkId;
                return id ? [[id, element] as const] : [];
              }),
            ),
          };
          chunkElementsCacheRef.current = cache;
        }

        const containerTop = container.getBoundingClientRect().top;
        const currentId = computeCurrentOrderedSectionId(
          cache.chunkIds,
          () => container.scrollTop,
          (id) => {
            const element = cache.elements.get(id);
            if (!element) return null;
            return (
              element.getBoundingClientRect().top -
              containerTop +
              container.scrollTop
            );
          },
        );
        if (currentId) setSpyChunkId(currentId);
      });
    },
    [chunks],
  );

  useEffect(
    () => () => {
      if (scrollSpyFrameRef.current !== null) {
        window.cancelAnimationFrame(scrollSpyFrameRef.current);
      }
      cancelProgrammaticScroll();
    },
    [cancelProgrammaticScroll],
  );

  return { listContainerRef, handleTableScroll, scrollToChunk, spyChunkId };
};
